/**
 * External Imports
 */
import { connect, useDispatch } from 'react-redux';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { RootState } from '../../../redux/store';
/**
 * Internal Imports
 */
import EmpComplianceMetricChart from '../../../components/customMetricCharts/employee/EmpComplianceMetricChart';
import EmpTaskMetricChart from '../../../components/customMetricCharts/employee/EmpTaskMetricChart';
import EmpRecognitionMetricChart from '../../../components/customMetricCharts/employee/EmpRecognitionMetricChart';
import EmpCoachingMetricChart from '../../../components/customMetricCharts/employee/EmpCoachingMetricChart.tsx';
import EmpStoplightMetricChart from '../../../components/customMetricCharts/employee/EmpStoplightMetricChart';
import SummaryReportHeader from '../../../components/searchHeader/summaryReportHeader';
import NoDataSplashView from '../../../components/shared/table/noDataSplashView';
import Loading from '../../../components/shared/Loading';
import {
  useFeedbackApi,
  useIssuesApi,
  useStoplightsApi
} from '../../../utils/api';
import { setDelegatedEmployee, setUserSection } from '../../../redux/actions';
/**
 * Global Definition Imports
 */
import {
  AuthUserProps,
  EmpDepartment,
  Filter
} from '../../../config/interfaces';
/**
 * Style Imports
 */
import {
  CTSMetricChartsContainer,
  SummaryReportContainer,
  TSMetricChartsContainer
} from '../styles/ReportsViews.styles';

interface EmployeeSummaryReportProps {
  authorizedUser: AuthUserProps;
  employeeSummaryReportFilter: Filter;
  selectedEmpDepts: EmpDepartment[];
  allEmpDepts: EmpDepartment[];
  isIssuesLoading: boolean;
  isFeedbackLoading: boolean;
  isStoplightLoading: boolean;
}

const EmployeeSummaryReport: FC<EmployeeSummaryReportProps> = ({
  authorizedUser,
  employeeSummaryReportFilter,
  selectedEmpDepts,
  allEmpDepts,
  isIssuesLoading,
  isFeedbackLoading,
  isStoplightLoading
}) => {
  const dispatch = useDispatch();
  const [initialRender, setInitialRender] = useState(false);

  const callForMetrics = useCallback(async () => {
    const issueFeedbackParams = {
      authorizedUser,
      searchTerm: '',
      selectedEmpDepartments: selectedEmpDepts,
      sortDir: 'desc',
      sortKey: 'created',
      selectedDelegate: authorizedUser.facilityId,
      summaryView: 'employee',
      selectedUnits: []
    };

    dispatch(
      await useIssuesApi({
        ...issueFeedbackParams,
        currentIssuePage: 1,
        issueFilter: employeeSummaryReportFilter
      })
    );
    dispatch(
      await useFeedbackApi({
        ...issueFeedbackParams,
        currentFeedbackPage: 1,
        feedbackFilter: {
          ...employeeSummaryReportFilter,
          taskSubType: 'coaching'
        }
      })
    );
    dispatch(
      await useFeedbackApi({
        ...issueFeedbackParams,
        currentFeedbackPage: 1,
        feedbackFilter: {
          ...employeeSummaryReportFilter,
          taskSubType: 'recognition'
        }
      })
    );
    if (selectedEmpDepts.length === allEmpDepts.length) {
      dispatch(
        useStoplightsApi({
          ...issueFeedbackParams,
          stoplightFilter: employeeSummaryReportFilter,
          currentStoplightPage: 1
        })
      );
    }
  }, [
    allEmpDepts,
    authorizedUser,
    employeeSummaryReportFilter,
    selectedEmpDepts
  ]);

  const isAnyChartsLoading = useMemo(
    () =>
      isIssuesLoading ||
      isFeedbackLoading ||
      isStoplightLoading ||
      !initialRender,
    [isIssuesLoading, isFeedbackLoading, isStoplightLoading, initialRender]
  );

  useEffect(() => {
    dispatch(
      setDelegatedEmployee({
        employeeName: 'Facility List',
        hcaid: authorizedUser.coid
      })
    );
    dispatch(setUserSection('reports'));
    // summary report filter is persistent, so this triggers a re-fetch just in case any thing changed
    setInitialRender(true);
  }, []);

  useEffect(() => {
    callForMetrics();
  }, [callForMetrics]);

  return (
    <SummaryReportContainer>
      <SummaryReportHeader />
      {selectedEmpDepts.length === 0 ? (
        <NoDataSplashView type="patientSummaryReport" />
      ) : isAnyChartsLoading ? (
        <Loading centered={false} />
      ) : (
        <CTSMetricChartsContainer>
          <EmpComplianceMetricChart facilityId={authorizedUser.facilityId} />
          <TSMetricChartsContainer>
            <EmpTaskMetricChart />
            <EmpStoplightMetricChart
              isFacilityList={selectedEmpDepts.length === allEmpDepts.length}
            />
            <EmpRecognitionMetricChart />
            <EmpCoachingMetricChart />
          </TSMetricChartsContainer>
        </CTSMetricChartsContainer>
      )}
    </SummaryReportContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser,
    employeeSummaryReportFilter:
      state.ReportReducer.employeeSummaryReportFilter,
    selectedEmpDepts: state.ConfigReducer.selectedEmpDepartments,
    allEmpDepts: state.ConfigReducer.empDepartments,
    isIssuesLoading: state.TaskReducer.loadingIssuesByUnitIdsOrEmployeeList,
    isFeedbackLoading: state.TaskReducer.loadingFeedbackByUnitIds,
    isStoplightLoading: state.StoplightReducer.loading
  };
};

export default connect(mapReduxStateToProps)(EmployeeSummaryReport);
